import {
  EffectiveDateType,
  IOfferingRes,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  IRateResBaseSchema,
  OfferingTypesEnum,
  RateBillingFrequencyEnum,
} from '@monetize/types/app';

import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { differenceInCalendarMonths } from 'date-fns/differenceInCalendarMonths';
import { formatISO } from 'date-fns/formatISO';
import { getDaysInMonth } from 'date-fns/getDaysInMonth';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { parseISO } from 'date-fns/parseISO';
import { setDate } from 'date-fns/setDate';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { IoMdCalendar } from 'react-icons/io';
import {
  MBox,
  MButton,
  MCloseButton,
  MFlex,
  MFormField,
  MGrid,
  MIcon,
  MText,
} from '../../../../../components/Monetize';
import {
  DatePicker,
  DatePickerProps,
} from '../../../../../components/Monetize/DatePicker/DatePicker';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { getBillingAnchorIfExists } from '../../quoteUtils';
import QuoteOfferingRateRightEl from './QuoteOfferingRateRightEl';
import {
  getIsUsingDelayedBilling,
  getRateRightElement,
  hasUsageProducts,
} from './quoteOfferingUtils';

export interface QuoteOfferingScheduledChangeProps {
  initialDate: Date;
  quote: IQuoteRespSchema;
  quoteOffering: IQuoteOfferingRespSchema;
  parentQuoteOffering?: IQuoteOfferingRespSchema | null;
  offeringRatesById: Record<string, IRateResBaseSchema>;
  loading?: boolean;
  inactiveRate?: IRateResBaseSchema;
  onSubmit: (value: {
    startDate: string;
    endDate: string;
    rateId: string;
  }) => void;
  onCancel: () => void;
  availableMonthSelectOptions: {
    title: string;
    value: EffectiveDateType;
  }[];
  offering: IOfferingRes;
  monthInterval: number;
  childQuoteOfferings?: IQuoteOfferingRespSchema[];
  gridColumns: string;
  rateSelects: [IRateResBaseSchema[], IRateResBaseSchema[]];
  updatePrevScheduleChange: ({ endDate }: { endDate: string }) => void;
}

/**
 * This component allows a user to add a new scheduled change
 * This is in charge of the initial date selection and the rate selection
 */
export const QuoteOfferingCreateScheduleChange = ({
  initialDate,
  quote,
  quoteOffering,
  parentQuoteOffering,
  offeringRatesById,
  inactiveRate,
  loading,
  onSubmit,
  onCancel,
  availableMonthSelectOptions,
  offering,
  monthInterval,
  childQuoteOfferings,
  gridColumns,
  rateSelects,
  updatePrevScheduleChange,
}: QuoteOfferingScheduledChangeProps) => {
  const [hasDateSelection, setHasDateSelection] = useState(false);
  const [autoOpenDatePicker, setAutoOpenDatePicker] = useState(false);
  const isMinCommit = offering.type === OfferingTypesEnum.MIN_COMMIT;

  const hasUsageProduct = hasUsageProducts(quoteOffering, offering);

  const isUsingDelayedBilling = getIsUsingDelayedBilling(quote);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<{
    startDate: string | undefined;
    rateId: string;
    endDate: string;
  }>({
    mode: 'onChange',
    reValidateMode: 'onChange',

    defaultValues: {
      // startDate: formatISO(addDays(initialDate, 1), { representation: 'date' }),
      endDate: quote.contractEndDate,
      rateId: quoteOffering.rateId,
    },
  });

  const startDateWatch = watch('startDate');
  const endDateWatch = watch('endDate');

  const getAnchoredPeriodEndInSelectedMonth = (selectedDateIso: string) => {
    const selected = parseISO(selectedDateIso);
    const anchorDay = parseISO(quoteOffering.startDate).getDate();
    const endDayRaw =
      anchorDay === 1 ? getDaysInMonth(selected) : anchorDay - 1;

    const clampedEndDay = Math.min(endDayRaw, getDaysInMonth(selected));
    return setDate(selected, clampedEndDay);
  };

  useEffect(() => {
    if (!startDateWatch) {
      return;
    }

    // find endDate in childQuoteOfferings
    const foundOffering = (
      [parentQuoteOffering, ...(childQuoteOfferings || [])].filter(
        (offering) => !!offering,
      ) as IQuoteOfferingRespSchema[]
    ).find(
      ({ startDate, endDate }) =>
        startDateWatch &&
        isBefore(parseISO(startDateWatch), parseISO(endDate)) &&
        isBefore(parseISO(startDate), parseISO(startDateWatch)),
    );

    const newEndDate = foundOffering
      ? foundOffering.endDate
      : quote.contractEndDate;

    setValue('endDate', newEndDate);
  }, [
    startDateWatch,
    childQuoteOfferings,
    parentQuoteOffering,
    setValue,
    quote.contractEndDate,
  ]);

  function handleStateDateDropdownChange(value: EffectiveDateType) {
    const isValueNumber = typeof value === 'number';

    // TODO: do we need to do this?
    // const parentOfferingHasUsage = offering?.products?.some(
    //   (product) => product.productType === ProductTypeEnum.USAGE,
    // );
    // const shouldUseBillingAnchor =
    //   quote?.billingAnchor?.day && (isMinCommit || parentOfferingHasUsage);

    // Step 1: Determine base start and end date based on the root quote offering start date OR the billing anchor if it exists
    // We start here to make sure we account for offerings that start on the last day of the month
    // this ensures that we correctly add billing periods (e.g. adding 2 months to Jan 31 (correct) is different from adding 1 month to Feb 28 (incorrect))
    const rootOfferingStartDate = parseISO(
      parentQuoteOffering?.startDate || quote.contractStartDate,
    );
    const offeringStartDate = parseISO(quoteOffering.startDate);
    const rootStartDate = getBillingAnchorIfExists(
      rootOfferingStartDate,
      quote.billingAnchor,
    );

    // Add in the offset from the billing anchor or quote offering start date
    if (
      typeof value === 'number' &&
      !isSameDay(offeringStartDate, rootStartDate)
    ) {
      value += Math.max(
        differenceInCalendarMonths(offeringStartDate, rootStartDate),
        0,
      );
    }

    const startDate = isValueNumber
      ? addMonths(rootStartDate, value as number)
      : addDays(initialDate, 1);

    const prevScheduleEndDate = isValueNumber
      ? addDays(startDate, -1)
      : initialDate;

    // Step 3: Update form and local state
    setValue('startDate', formatISO(startDate, { representation: 'date' }));
    updatePrevScheduleChange({
      endDate: formatISO(prevScheduleEndDate, { representation: 'date' }),
    });
    setAutoOpenDatePicker(value === 'CUSTOM');
    setHasDateSelection(true);
  }

  const handleCancel = () => {
    // Update local state - find the previous segment end date and reset
    const prevQuoteOffering = quote?.quoteOfferings.find(
      (quoteOff) => quoteOff.id === quoteOffering.id,
    );
    if (prevQuoteOffering) {
      updatePrevScheduleChange({
        endDate: prevQuoteOffering.endDate,
      });
    }
    onCancel();
  };

  const datepickerOptions: Partial<DatePickerProps> = {
    minDate: quoteOffering?.startDate
      ? addDays(parseISO(quoteOffering?.startDate), 1)
      : addDays(parseISO(quote.contractStartDate), 1),
    maxDate: addDays(parseISO(quote.contractEndDate), 0),
  };
  const startDatePickerOptions: Partial<DatePickerProps> = {};
  const endDatePickerOptions: Partial<DatePickerProps> = {};

  // Restrict mode based on use-case
  if (isMinCommit) {
    datepickerOptions.mode =
      quoteOffering.billingFrequency === RateBillingFrequencyEnum.ANNUALLY
        ? 'YEAR'
        : 'MONTH';
    datepickerOptions.interval =
      quoteOffering.billingFrequency === RateBillingFrequencyEnum.ANNUALLY
        ? 1
        : (monthInterval as 1 | 3 | 6); // This will be only 1 or 3 or 6
  } else if (hasUsageProduct) {
    datepickerOptions.mode = 'MONTH';
    datepickerOptions.interval = 1;
  }

  if (isUsingDelayedBilling) {
    datepickerOptions.mode = 'YEAR';
    datepickerOptions.interval = 1;
  }

  if (isMinCommit || hasUsageProduct || isUsingDelayedBilling) {
    // Last frequency period cannot be selected
    datepickerOptions.minDate = addMonths(initialDate, 1 * monthInterval);
    datepickerOptions.maxDate = parseISO(quote.contractEndDate);
    datepickerOptions.baseDate = initialDate;
    datepickerOptions.anchorMonth = initialDate.getMonth() + 1;
  } else {
    startDatePickerOptions.maxDate = parseISO(endDateWatch);
    startDateWatch && (endDatePickerOptions.minDate = parseISO(startDateWatch));
  }

  return (
    <MGrid
      bg={'tWhite.base'}
      alignItems="center"
      gridTemplateColumns={gridColumns}
      columnGap={6}
      pl="52px"
      py="2"
    >
      <MFlex gridColumn={'1 / 3'} direction="row" alignItems="flex-end">
        <MFormField
          alignSelf="center"
          label="Rate"
          error={errors.rateId}
          maxW="10.25rem"
        >
          <Controller
            control={control}
            name="rateId"
            render={({ field }) => (
              <MCustomSelect
                isDisabled={loading}
                maxW="187px"
                placeholder="Select Rate"
                items={rateSelects[0]}
                itemTitle="name"
                itemValue="id"
                renderRightElement={({ value }) =>
                  getRateRightElement({
                    value,
                    offeringRatesById,
                  })
                }
                renderItemContent={({ title, isDisabled, item }) => {
                  return (
                    <MFlex alignItems="center" gap={2}>
                      <QuoteOfferingRateRightEl rate={item} />
                      <MText>{title}</MText>
                    </MFlex>
                  );
                }}
                {...field}
              />
            )}
          />
        </MFormField>

        {!hasDateSelection ? (
          <MFormField
            alignSelf="center"
            label="Start Date"
            maxW="10.25rem"
            ml="4"
          >
            <MCustomSelect
              placeholder="Select"
              items={availableMonthSelectOptions}
              renderInputLeftElement
              inputLeftElementContent={<MIcon as={IoMdCalendar} w="5" h="5" />}
              onChange={handleStateDateDropdownChange}
            />
          </MFormField>
        ) : (
          <MFormField
            alignSelf="center"
            label="Start Date"
            maxW="10.25rem"
            ml="4"
            error={errors.startDate}
          >
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, ...rest } }) => (
                <DatePicker
                  {...rest}
                  {...datepickerOptions}
                  {...startDatePickerOptions}
                  autoOpen={autoOpenDatePicker}
                  isDisabled={loading}
                  onChange={(date) => {
                    onChange(date);
                    if (!date) {
                      return;
                    }
                    updatePrevScheduleChange({
                      endDate: formatISO(addDays(parseISO(date), -1), {
                        representation: 'date',
                      }),
                    });
                  }}
                />
              )}
            />
          </MFormField>
        )}

        <MFormField
          alignSelf="center"
          label="End Date"
          maxW="10.25rem"
          error={errors.endDate}
          ml="4"
        >
          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, ...rest } }) => (
              <DatePicker
                {...rest}
                {...datepickerOptions}
                {...endDatePickerOptions}
                isDisabled={loading}
                onChange={(date) => {
                  if (!date) {
                    onChange(date);
                    return;
                  }

                  if (isMinCommit || hasUsageProduct || isUsingDelayedBilling) {
                    const endOfPeriod = formatISO(
                      getAnchoredPeriodEndInSelectedMonth(date),
                      { representation: 'date' },
                    );
                    onChange(endOfPeriod);
                    return;
                  }
                  onChange(date);
                }}
              />
            )}
          />
        </MFormField>
        <MBox ml="4">
          <MButton
            data-testid="scheduled-change-add-button"
            variant="primary"
            size="sm"
            isLoading={loading}
            isDisabled={!isValid || loading || !startDateWatch}
            onClick={handleSubmit((data) => {
              if (!!data.startDate) {
                onSubmit({
                  rateId: data.rateId,
                  startDate: data.startDate,
                  endDate: data.endDate,
                });
              }
            })}
            type="button"
          >
            Add
          </MButton>
        </MBox>
      </MFlex>

      <MFlex gridColumn={'4 / -1'} justifyContent="flex-end" height="100%">
        {!loading && (
          <MCloseButton
            alignSelf="end"
            justifySelf="end"
            minH="7"
            minW="8"
            onClick={() => handleCancel()}
          />
        )}
      </MFlex>
    </MGrid>
  );
};
